import { Phone, MapPin, Clock, Navigation } from "lucide-react";
import { MaskReveal, Reveal } from "../components/Reveal";
import { BookingForm } from "../components/BookingForm";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { Seo } from "../components/Seo";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  ADDRESS,
  HOURS,
  MAPS_LINK,
  MAP_EMBED,
} from "../data/siteData";

const InfoRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-start gap-5 border-b border-white/5 py-7" data-testid={testid}>
    <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-400/5">
      <Icon className="h-5 w-5 text-amber-300" strokeWidth={1.6} />
    </span>
    <div>
      <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-stone-500">{label}</p>
      <div className="mt-2">{children}</div>
    </div>
  </div>
);

export default function Contact() {
  return (
    <>
      <Seo
        title="Book a Massage in Suchitra | Call 098666 34473 | Belong Spa"
        description="Book your massage at Belong Salon & Spa, Suchitra, Hyderabad — call 098666 34473 or WhatsApp. 1st Floor, Plot 121, Quthbullapur Main Rd, opp. Bharat Petrol Pump, Suchitra."
        keywords="book massage Suchitra, spa contact Hyderabad, Belong spa phone number, massage booking Quthbullapur, spa near Bharat Petrol Pump Suchitra"
      />
      <section className="relative overflow-hidden pb-20 pt-44" data-testid="contact-hero">
        <div className="ambient-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskReveal delay={0.15}>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.35em] text-amber-400/80">Contact Us</p>
          </MaskReveal>
          <h1 className="mt-8 font-display text-4xl font-light leading-[1.06] tracking-tight text-amber-50 sm:text-6xl lg:text-7xl">
            <MaskReveal delay={0.3}>Begin your</MaskReveal>
            <MaskReveal delay={0.45}>
              <em className="text-gold-gradient not-italic">session.</em>
            </MaskReveal>
          </h1>
          <MaskReveal delay={0.65} className="mt-8 max-w-xl">
            <p className="text-base font-light leading-relaxed text-stone-400 sm:text-lg">
              Call, message, or simply walk up the stairs above Suchitra’s main road. The kettle is
              always on.
            </p>
          </MaskReveal>
        </div>
      </section>

      <section id="booking" className="border-y border-amber-500/10 bg-[#0D0D0F] py-24" data-testid="booking-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Booking Form</p>
            <h2 className="mt-5 font-display text-3xl font-light leading-[1.12] tracking-tight text-amber-50 sm:text-4xl">
              Reserve your slot <em className="text-gold-gradient not-italic">in seconds.</em>
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-stone-400">
              Pick a service, a duration and a time slot — your request lands directly on our
              WhatsApp with everything pre-filled. We confirm your suite within minutes.
            </p>
            <p className="mt-8 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500">
              Prefer to talk? {PHONE_DISPLAY}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <BookingForm />
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="contact-details">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <InfoRow icon={Phone} label="Call Us" testid="contact-phone-row">
              <a
                href={PHONE_TEL}
                data-testid="contact-phone-link"
                className="font-display text-3xl font-light text-amber-100 transition-colors hover:text-amber-300"
              >
                {PHONE_DISPLAY}
              </a>
            </InfoRow>

            <InfoRow icon={MapPin} label="Find Us" testid="contact-address-row">
              <p className="max-w-md text-sm font-light leading-relaxed text-stone-300">{ADDRESS}</p>
              <a
                href={MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-link"
                className="mt-4 inline-flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-amber-300 transition-colors hover:text-amber-100"
              >
                <Navigation className="h-3.5 w-3.5" />
                Get Directions
              </a>
            </InfoRow>

            <InfoRow icon={Clock} label="Hours" testid="contact-hours-row">
              <p className="font-display text-2xl font-light text-amber-100" data-testid="contact-hours-text">
                {HOURS}
              </p>
              <p className="mt-2 text-sm font-light text-stone-500">
                Walk-ins welcome · Advance booking recommended on weekends
              </p>
            </InfoRow>

            <div className="pt-8">
              <WhatsAppButton testid="contact-whatsapp-booking-btn" label="Message on WhatsApp" />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              className="spotlight-frame h-full min-h-[420px] overflow-hidden rounded-3xl border border-amber-500/20"
              data-testid="contact-map"
            >
              <iframe
                title="Belong Salon & Spa location map"
                src={MAP_EMBED}
                className="h-full min-h-[420px] w-full grayscale-[35%] invert-[92%] hue-rotate-180 contrast-[0.9]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
